import type { IndividualAggregate } from "@/api/researchCms.service";
import {
  UNRESOLVED_FIRM_LABEL,
  type OrgRowKind,
  type TableContent,
  type TableRanking,
} from "./orgRows";

export type LawyerRow = {
  contactUuid: string;
  companyUuid: string;
  name: string;
  organisationName: string;
  rowKind: OrgRowKind;
  reviewStatus: string | null;
  hasSubmission: boolean;
  currentTier: number | null;
  currentRankingType: string | null;
  previousTier: number | null;
  previousRankingType: string | null;
  // Per-table truth behind the scalars above — one entry per lawyer table the BPA has.
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  // A "field" is one client, contact or matter entry, counted the same way as on the
  // organisations tab so the two tabs agree.
  populatedFields: number;
  totalFields: number;
  // Tables this lawyer holds editorial content for, independent of ranking.
  contentTableIds: string[];
  // Per-table content figures — see orgRows.ts's OrgListApiRow.
  contentByTable: Record<string, TableContent>;
};

function toLawyerRow(row: IndividualAggregate): LawyerRow {
  return {
    contactUuid: row.contactId,
    companyUuid: row.companyId,
    name: row.contactName ?? row.contactId,
    organisationName: row.companyName ?? UNRESOLVED_FIRM_LABEL,
    rowKind: row.rowKind,
    reviewStatus: row.reviewStatus,
    hasSubmission: row.hasSubmission,
    currentTier: row.currentTier,
    currentRankingType: row.currentRankingType,
    previousTier: row.previousTier,
    previousRankingType: row.previousRankingType,
    currentRankings: row.currentRankings,
    previousRankings: row.previousRankings,
    populatedFields: row.populatedFields,
    totalFields: row.totalFields,
    contentTableIds: row.contentTableIds,
    contentByTable: row.contentByTable,
  };
}

/**
 * Organisation-level rows carry no contact; dropping any that reach here keeps contactUuid
 * non-nullable for the row key and the individual route. A lawyer named by more than one source
 * arrives once per source, so rows are also deduped by contact, first occurrence wins.
 */
export function toLawyerRows(rows: IndividualAggregate[]): LawyerRow[] {
  const rowsByContact = new Map<string, LawyerRow>();

  for (const row of rows) {
    if (row.contactId === null) continue;
    if (rowsByContact.has(row.contactId)) continue;
    rowsByContact.set(row.contactId, toLawyerRow(row));
  }

  return [...rowsByContact.values()];
}
